<template>
  <label
    ref="labelRef"
    class="q-select"
    :class="[
      $attrs.class,
      {
        'q-select__multiline': multiline,
        'q-select__loading': isLoading,
        'q-select__full-selected': fullWidthSelected,
        'q-select__with-icon': !!$slots.icon && !redesigned,
        'q-select__searchable': searchable,
        'q-select--redesigned': redesigned,
        'q-select--condensed': condensed,
        'q-select--borderless': borderless,
        'q-select--open': isOpen,
        'q-select--error': error,
      },
    ]"
    :input-id="inputId"
    @input.stop
  >
    <global-events no-filtering @keydown.shift.tab.exact="handleTabPress" @keydown.tab.exact="handleTabPress" />
    <span v-if="fieldLabel" class="flex items-center justify-between q-select__label">{{ fieldLabel }}</span>

    <span
      class="text-sm font-normal q-select__field"
      :class="{
        'q-select__field--disabled': disabled,
        dark: theme === 'dark',
      }"
    >
      <span v-if="$slots.icon" class="q-select__field-icon">
        <slot name="icon" :open="isOpen" />
      </span>
      <multiselect
        :id="inputId"
        v-bind="selectAttrs"
        ref="select"
        :value="cloneDeep(toRaw(modelValue))"
        :disabled="disabled"
        :value-prop="valueProp"
        :hide-selected="false"
        :can-clear="canClear"
        :can-deselect="false"
        :clear-on-select="clearOnSelect"
        :placeholder="placeholder"
        :no-results-text="noResultsText || $t('universal.no_items_found')"
        :no-options-text="noOptionsText"
        :label="label"
        :searchable="searchable"
        :options="options"
        :open-direction="computedOpenDirection"
        class="text-sm"
        :class="{
          'font-medium': condensed,
          'font-normal': !condensed,
        }"
        :classes="{
          dropdown: condensed ? 'multiselect-dropdown text-xs leading-5' : 'multiselect-dropdown text-sm',
          groupLabel: 'multiselect-group-label text-xs',
          optionDisabled: showDisabledCursor ? 'cursor-not-allowed' : 'pointer-events-none',
          optionPointed: 'bg-transparent',
        }"
        v-on="listeners"
        @update:model-value="updateValue"
        @click.prevent
        @open="handleOpen"
        @close="handleClose"
      >
        <template #option="{ option, isSelected }">
          <div :class="$style.optionWrapper" @click="handleOptionClick(option, isSelected(option), $event)">
            <slot name="option" :option="option" :selected="isSelected(option)">
              <component :is="option.icon" v-if="option.icon" :class="$style.userIcon" />
              <q-avatar
                v-else-if="'avatar' in option"
                :size="24"
                :src="typeof option.avatar === 'string' ? option.avatar : undefined"
                :component="typeof option.avatar === 'object' ? option.avatar : undefined"
                :username="option[label]"
                :class="$style.avatar"
              />
              <slot name="option-label" :option="option" :label="option[label]">
                <span class="w-full truncate" :title="option[label]">{{ option[label] }}</span>
              </slot>
              <check-icon v-if="isSelected(option)" :class="['text-active-primary', condensed && $style.smallIcon]" />
            </slot>
          </div>
        </template>
        <template #tag="{ option, handleTagRemove }">
          <span data-testid="multiselect-tag" class="multiselect-tag">
            <slot v-if="$slots.tag" name="tag" :option="option" />
            <span v-else>{{ option[label] }}</span>
            <span class="multiselect-tag-remove" @click="(event) => handleTagRemove(option, event)">
              <x-icon class="icon-m" />
            </span>
          </span>
        </template>
        <template #multiplelabel="values">
          <div :class="$style.label">
            <slot name="multiplelabel" :values="values" />
          </div>
        </template>
        <template #singlelabel="values">
          <div v-if="$slots.singlelabel" :class="$style.label">
            <slot name="singlelabel" :value="values.value" />
          </div>
        </template>
        <template #beforelist>
          <div v-if="$slots.beforelist" class="text-xs" :class="$style.header">
            <slot name="beforelist" />
          </div>
        </template>
        <template #afterlist>
          <div v-if="$slots.afterlist" :class="$style.more">
            <slot name="afterlist" />
          </div>
        </template>
      </multiselect>
    </span>
  </label>
</template>

<script lang="ts">
import { defineComponent, computed, nextTick, ref, onMounted, onBeforeUnmount, watch, type PropType, toRaw } from 'vue'
import { CheckIcon, XIcon } from '@klausapp/ui-kit/icons'
import Multiselect from '@vueform/multiselect'
import '@vueform/multiselect/themes/default.css'
import { createPopper, type Instance } from '@popperjs/core'
import { type Placement } from 'tippy.js'
import { onClickOutside, useElementBounding } from '@vueuse/core'
import { cloneDeep, omit } from 'lodash-es'

import { GlobalEvents } from '@klausapp/ui-kit'
import QAvatar from '@/components/QAvatar/QAvatar.vue'
import { sanitize } from '@/utils/sanitize'

export default defineComponent({
  name: 'QSelect',
  components: { Multiselect, CheckIcon, XIcon, GlobalEvents, QAvatar },
  inheritAttrs: false,
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    modelValue: [Number, String, Array, Object, Date],
    label: {
      type: String,
      default: 'name',
    },
    clearOnSelect: {
      type: Boolean,
      default: false,
    },
    fieldLabel: String,
    multiline: Boolean,
    noResultsText: String,
    opened: Boolean,
    isLoading: Boolean,
    noOptionsText: String,
    disabled: Boolean,
    fullWidthSelected: Boolean,
    placeholder: String,
    redesigned: Boolean,
    borderless: Boolean,
    condensed: Boolean,
    error: Boolean,
    searchable: {
      type: Boolean,
      default: true,
    },
    canClear: Boolean,
    theme: {
      type: String,
      default: 'light',
    },
    valueProp: {
      type: String,
      default: 'id',
    },
    inputId: String,
    appendToBody: Boolean,
    width: Number,
    offset: {
      type: Array as PropType<number[]>,
      default: () => [0, 0],
    },
    options: {
      type: Array as PropType<any[]>,
      required: true,
    },
    padding: {
      type: Number,
      default: 5,
    },
    openDirection: {
      type: String as PropType<'top' | 'bottom' | 'auto'>,
      default: 'bottom',
    },
    showDisabledCursor: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'open', 'dropdown-close'],
  setup(props, { attrs: allListeners, emit, attrs }) {
    const labelRef = ref<HTMLLabelElement>()
    const isOpen = ref(props.opened)
    const select = ref<any | null>(null)
    const dropdown = ref<HTMLDivElement>()
    const instance = ref<Instance>()

    const listeners = computed(() => {
      const { 'onUpdate:modelValue': update, ...others } = allListeners
      return { ...others }
    })

    const computedOpenDirection = computed(() => {
      if (props.openDirection === 'auto') {
        const triggerEl = select.value?.$refs.multiselect
        if (!triggerEl) return props.openDirection
        const { placement } = getPositioning(triggerEl)
        return placement
      }

      return props.openDirection
    })

    onMounted(async () => {
      select.value.$refs.search?.addEventListener('blur', () => {
        if (select.value?.search) select.value.search = ''
      })

      if (props.opened) {
        await nextTick()
        select.value?.open()
        focus()
      }
    })

    const blur = () => select.value?.$refs?.input?.blur()

    const focus = () => select.value?.$refs?.input?.focus()

    const selectAttrs = computed(() => omit(attrs, 'class'))

    const close = async () => {
      await nextTick()
      select.value?.close()
      blur()
    }

    const open = async () => {
      await nextTick()
      select.value?.open()
      focus()
    }

    const getDropdown = () => {
      // try to get dropdown body from multiselect
      const elements = select.value.$el.getElementsByClassName('multiselect-dropdown')
      if (elements.length) return elements[0]

      // try to get dropdown body from document body since it wasn't present in multiselect
      const dropdownContainer = document.getElementById('dropdown')
      if (!dropdownContainer) return null

      return dropdownContainer.getElementsByClassName('multiselect-dropdown')[0]
    }

    const getPositioning = (triggerEl: HTMLDivElement): { placement: Placement; distance: number } => {
      const { height: triggerHeight, y: triggerY } = triggerEl.getBoundingClientRect()
      const spaceBelow = window.innerHeight - triggerY - triggerHeight
      const spaceAbove = triggerY
      const maxSpace = Math.max(spaceBelow, spaceAbove)

      return { placement: spaceBelow > spaceAbove ? 'bottom' : 'top', distance: maxSpace }
    }

    const { width: inputWidth } = useElementBounding(labelRef)

    const moveDropdownToBody = () => {
      const triggerEl = select.value.$refs.multiselect
      const dropdownEl = getDropdown()
      dropdown.value = dropdownEl
      if (!dropdownEl) return

      const container = document.createElement('div')
      container.id = 'dropdown'
      container.classList.add('q-select', 'q-select__field', ...(attrs.class as string).split(' '))
      const multiselect = document.createElement('div')
      multiselect.classList.add('multiselect')
      multiselect.classList.add('is-open')
      container.append(multiselect)
      multiselect.append(dropdownEl)
      document.body.append(container)
      dropdownEl.style.width = `${props.width || inputWidth.value}px`

      const { placement, distance } = getPositioning(triggerEl)
      dropdownEl.style['max-height'] = `${distance - props.padding}px`

      if (!instance.value)
        instance.value = createPopper(triggerEl, dropdownEl, {
          placement,
          modifiers: [{ name: 'offset', options: { offset: props.offset } }],
        })
    }

    watch(inputWidth, (width) => {
      const dropdownEl = getDropdown()

      if (dropdownEl && props.appendToBody && !props.width) {
        ;(dropdownEl as HTMLElement).style.width = `${width}px`
        instance.value?.update()
      }
    })

    const removeDropdownFromBody = () => {
      const dropdownContainer = document.getElementById('dropdown')
      if (!dropdownContainer) return

      const dropdownEl = dropdownContainer.getElementsByClassName('multiselect-dropdown')[0]
      if (!dropdownEl) return

      select.value?.$el.append(dropdownEl)
      dropdownContainer.remove()
      dropdown.value = undefined
      popperCleanup()
    }

    const popperCleanup = () => {
      instance.value?.destroy()
      instance.value = undefined
    }

    const handleOpen = () => {
      if (isOpen.value) return

      isOpen.value = true
      emit('open')

      if (props.appendToBody) moveDropdownToBody()
    }

    const emitClose = () => {
      isOpen.value = false
      emit('dropdown-close')
    }

    const handleClose = () => {
      if (!props.appendToBody) emitClose()
    }

    const handleBodyDropdownClose = () => {
      removeDropdownFromBody()
      emitClose()
    }
    /**
     * Close event is fired before update event from multiselect, so dropdown cannot
     * be removed from body on close event, otherwise mouse clicks won't go throught
     * and items aren't selected correctly. Because of that there's an external observer for
     * cases when dropdown is appended to body.
     */
    onClickOutside(dropdown, (e) => {
      if (props.appendToBody && !labelRef.value?.contains(e.target as Node)) {
        handleBodyDropdownClose()
      }
    })

    const updateValue = (value) => {
      emit('update:modelValue', typeof value === 'string' ? sanitize(value.trim()) : value)
      if (props.appendToBody) {
        select.value.close()
        handleBodyDropdownClose()
      }
    }

    const handleTabPress = () => {
      if (props.appendToBody && isOpen.value) handleBodyDropdownClose()
    }

    const handleOptionClick = (option, isSelected: boolean, event) => {
      if (option.placeholder || option.name === 'separator') return event.stopPropagation()

      // multiselect doesn't fire update action when clicking a selected item and we can't
      // rely on close action alone, as it comes before update and there's no way to tell if something
      // was selected or dropdown was just closed
      if (isSelected && props.appendToBody) {
        handleBodyDropdownClose()
      }
    }

    onBeforeUnmount(() => {
      if (props.appendToBody) removeDropdownFromBody()
      popperCleanup()
    })

    watch(isOpen, async (isOpen) => {
      if (isOpen) {
        await nextTick()
        const dropdown = getDropdown()
        if (!dropdown) return
        dropdown.scrollTop = 0
      }
    })

    watch(
      () => props.options,
      async (options) => {
        if (!isOpen.value || !options.length) return

        await nextTick()
        select.value.setPointer(options[0])
      },
    )

    return {
      blur,
      close,
      open,
      computedOpenDirection,
      labelRef,
      listeners,
      select,
      updateValue,
      handleOpen,
      handleClose,
      handleOptionClick,
      handleTabPress,
      Deselect: XIcon,
      inputWidth,
      isOpen,
      selectAttrs,
      toRaw,
      cloneDeep,
    }
  },
})
</script>

<style lang="scss">
.q-select {
  display: inline-block;
  position: relative;
  background-color: var(--white);
  border-radius: var(--border-radius);

  &__field {
    position: relative;
    display: flex;
    align-items: center;
    height: 2.5rem;
    border: 1px solid var(--gray-30);
    border-radius: var(--border-radius);
    color: var(--gray-80);
    padding-left: 8px;
    transition:
      border-color 0.2s ease-in-out 0.1s,
      background-color 0.2s ease-in-out 0.1s;

    &:hover {
      border-color: var(--gray-40);
      cursor: pointer;
    }

    &::placeholder {
      color: var(--gray-50);
    }

    &:focus-within {
      border-color: theme('colors.active.primary');
      outline: none;
    }

    &--disabled {
      cursor: not-allowed;
      background-color: var(--gray-20);
      border-color: var(--gray-20);

      &:hover,
      &:focus-within {
        border-color: var(--gray-20);
      }

      .multiselect-option.is-selected {
        color: var(--gray-60);
      }
    }

    &.dark {
      background-color: var(--gray-10);

      .multiselect-tag {
        background-color: var(--white);
        border: 1px solid var(--gray-30);
      }
    }
  }
  &__field-icon {
    display: inherit;
    color: var(--gray-50);
    margin-left: 4px;
    margin-right: 4px;
  }
  &__label {
    width: 100%;
    margin-bottom: 0.25rem;
  }

  &__multiline &__field {
    height: auto;
    min-height: 2.5rem;
  }
  &--condensed &__field {
    height: 2rem;
  }
  &--borderless &__field {
    border: 1px solid var(--gray-30);
    &:hover {
      border: 1px solid var(--gray-40);
    }

    svg {
      color: var(--gray-60);
    }
  }

  &--borderless .multiselect-caret {
    mask-image: url('data:image/svg+xml,<svg width="13" height="14" viewBox="0 0 13 14" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Chevron"><path id="Vector" d="M3.25 5.78125L6.5 9.03125L9.75 5.78125" stroke="%23718096" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></g></svg>') !important;
  }
  &--redesigned &__field {
    border: none;
    border-bottom-left-radius: unset;
    border-bottom-right-radius: unset;
    padding: 0;
    height: unset;
  }
  &--redesigned &__field-icon {
    order: 2;
    margin-left: 8px;
  }
  &--redesigned .multiselect {
    min-height: unset;
  }
  &--redesigned .multiselect .multiselect-options .multiselect-option {
    padding: 4px 12px 4px 8px;
  }
  &--redesigned .multiselect .multiselect-dropdown {
    margin-left: -15px;
    width: 239px;
    border-top-left-radius: unset;
    border-top-right-radius: unset;
  }
  &--redesigned .multiselect .multiselect-placeholder,
  &--redesigned .multiselect .multiselect-search {
    padding: 0;
  }
  &--redesigned .multiselect-caret {
    display: none;
  }

  &--error &__field {
    border-color: var(--red-40);
    background-color: var(--red-10);

    input:focus {
      box-shadow: none;
    }
  }

  .q-select__field-icon + .multiselect,
  .q-select__field-icon + .multiselect > .multiselect-single-label {
    width: calc(100% - 24px);
  }

  .multiselect {
    position: relative;
    width: 100%;
    border: none;
    background-color: transparent;
    justify-content: flex-start;

    &.is-active {
      box-shadow: none;
      background-color: transparent !important;
    }

    .multiselect-option {
      justify-content: space-between;
      flex: 1;
      align-items: center;
      color: inherit;
      line-height: inherit;
      font-weight: theme('fontWeight.medium');
      font-size: inherit;
      border-radius: 0.25rem;
      margin: 0 0.25rem;
      padding: 0.5rem 0.75rem;
      margin-bottom: 2px;
      overflow: hidden;
      text-overflow: ellipsis;

      * {
        user-select: none;
      }

      &:not(.is-selected):hover {
        background-color: var(--gray-10);
      }
    }

    [id$='multiselect-option-separator'] {
      pointer-events: none;
      padding: 3px 0px;
    }
  }

  .multiselect-dropdown {
    cursor: default;
    border: none;
    border-radius: var(--border-radius);
    box-shadow: theme('boxShadow.md');
    left: -1px;
    margin-top: 6px;
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 364px;
    transform: translateY(calc(100% + 6px));
    padding: 5px 0;
    width: calc(100% + 8px);
    margin-left: -8px;

    &.is-top {
      transform: translateY(calc(-100% - 12px));
    }
  }

  &__with-icon .multiselect-dropdown {
    margin-left: -32px;
    width: calc(100% + 34px);
  }

  .multiselect-search {
    position: relative;
    padding-left: 4px;
  }

  .multiselect-option.is-selected {
    display: flex;
    color: var(--gray-80) !important;
    background-color: var(--active-secondary) !important;
    font-weight: theme('fontWeight.medium');
  }

  .multiselect-clear {
    height: 20px;

    svg {
      width: 20px;
      height: 20px;
      stroke: var(--gray-60);

      &:hover {
        stroke: var(--gray-70);
      }
    }
  }

  .multiselect-tags {
    padding: 3px 0;
    margin: 0;
  }

  .multiselect-tag {
    display: flex;
    color: var(--gray-80);
    background-color: var(--gray-20);
    line-height: 1.2;
    padding: 3.5px 0 3.5px 7px;
    font-weight: theme('fontWeight.medium');
    word-break: break-word;
    margin: 4px;
    white-space: normal;

    .multiselect-tag-remove {
      padding: 0 4px;

      &:hover {
        background-color: inherit;
      }
    }

    .multiselect-tag-remove-icon {
      color: var(--gray-50);
      height: 16px;
      stroke-width: 3px;
    }
  }

  .multiselect-tags-search,
  .multiselect-search {
    background-color: inherit;
  }

  .multiselect-placeholder {
    padding: 0 0 0 4px;
  }

  .multiselect-single-label {
    padding: 0 4px;
    position: relative;
    flex: 1;
  }

  &__searchable {
    .multiselect-single-label {
      position: absolute;
    }
  }

  .multiselect-search:focus-visible + .multiselect-single-label {
    opacity: 0.4;
  }

  .multiselect-no-options {
    text-align: center;
  }

  .multiselect-no-results {
    text-align: center;
  }

  .multiselect-caret {
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='%23718096' fill-rule='evenodd' d='M8.7 3.1L11.5 6c.3.3.1.8-.3.8H5.5A.5.5 0 015 6L8 3h.7zM8 13L5.2 10c-.3-.3-.1-.8.3-.8h5.7c.4 0 .6.5.3.8L8.7 13H8z' clip-rule='evenodd'/%3E%3C/svg%3E");
    width: 16px;
    height: 16px;
    margin-right: 8px;
    z-index: 0;
  }

  .multiselect-group-label {
    background-color: unset;
    color: var(--gray-60);
    padding: 8px 16px;
    font-weight: theme('fontWeight.medium');
  }
}

.q-select--borderless.q-select--open .q-select__field {
  border: 1px solid theme('colors.active.primary');
  background-color: var(--gray-10);
}

#dropdown,
#dropdown > .multiselect {
  display: block;
  height: 0;
  min-height: 0;
  border: 0;

  .multiselect-dropdown.is-hidden {
    display: block;
  }
}
</style>

<style lang="scss" module>
.label {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: 1;
  padding-left: 4px;
  position: absolute;
  width: calc(100% - 31px);
}

:global(.q-select--condensed) .label,
:global(.q-select--condensed .multiselect-single-label) {
  font-size: theme('fontSize.xs');
  font-weight: theme('fontWeight.medium');
}
:global(.multiselect.is-disabled) {
  cursor: inherit;

  .label {
    position: relative;
    color: var(--gray-60);
    background-color: transparent;
  }

  > :global(.multiselect-caret) {
    margin-left: auto;
  }
}

:global(.multiselect-search:focus-visible) + .label {
  opacity: 0.4;
}

.header:not(:empty) {
  color: var(--gray-60);
  padding: 8px 12px;
  margin: 0 4px 2px;
}

.more:not(:empty) {
  color: var(--gray-60);
  padding: 8px 12px;
  margin: 0 4px 2px;
}

.optionWrapper {
  display: inherit;
  justify-content: inherit;
  width: 100%;
  align-items: inherit;
  text-overflow: inherit;
  height: 1.5rem;
}

.avatar {
  margin-right: 8px;
}

.userIcon {
  height: 1rem;
  margin-left: 4px;
  margin-right: 10px;
}

.smallIcon {
  height: 16px;
  width: 16px;
}
</style>
